// Shape of an institution row from the API (search + /banks/:cert).
export type BankInfo = {
  cert: number;
  name: string;
  city: string | null;
  state: string | null;
};

// Search tab state, lifted to TaskPane so results survive navigating
// to Info and back. `searchedQuery` is the query the current results
// belong to (null until a search has run) — it lets the search page
// skip re-fetching on remount when the query hasn't changed.
export type SearchState = {
  query: string;
  searchedQuery: string | null;
  results: BankInfo[];
};

export type Entitlement = {
  tier: string | null;
  status: string;
};

// --- Screener ---

// Which control a screener field gets: free text, multi-select, or number+op.
export type ScreenKind = "text" | "multi" | "numeric";

// One criterion row. `fieldKey` is "<type>:<field>", e.g. "info:state" or
// "metric:ROA", so a single dropdown can span both field families.
export type ScreenCriterionRow = {
  id: number; // stable React key
  fieldKey: string;
  op: string; // contains|eq for text; lt|lte|eq|gte|gt for numeric
  value: string; // text/numeric input (raw string; parsed on submit)
  values: string[]; // multi-select choices
  quarter: string; // metric criteria only, e.g. "2021Q4"; "" = latest
};

// Extra column for the results sheet: a field plus an optional quarter
// (metrics only, "" = latest).
export type ScreenOutputColumn = {
  fieldKey: string;
  quarter: string;
};

export type ScreenResultBank = BankInfo & { asset: number | null };

// Screener state, lifted to TaskPane so criteria/results survive navigating
// to Info and back (same reasoning as SearchState).
export type ScreenerState = {
  logic: "and" | "or";
  criteria: ScreenCriterionRow[];
  outputs: ScreenOutputColumn[];
  results: ScreenResultBank[] | null; // null = screen not run yet
  total: number;
};

export const initialScreenerState = (): ScreenerState => ({
  logic: "and",
  criteria: [{ id: 1, fieldKey: "info:state", op: "eq", value: "", values: [], quarter: "" }],
  outputs: [],
  results: null,
  total: 0,
});

// --- Markets (Summary of Deposits) ---

// One geography from GET /api/markets/search, sized by the latest survey year.
export type MarketOption = {
  type: "state" | "county" | "msa" | "zip";
  code: string;
  name: string;
  branches: number;
  deposits: number; // thousands USD
};

// The market argument as written in a SHEETYBANKS.MARKET formula: bare state
// and county codes, prefixed MSA and zip codes.
export function marketFormulaCode(option: MarketOption): string {
  return option.type === "state" || option.type === "county"
    ? option.code
    : `${option.type}:${option.code}`;
}

// Markets tab state, lifted to TaskPane so the picked market survives
// navigating away (same reasoning as SearchState).
export type MarketsState = {
  query: string;
  searchedQuery: string | null;
  results: MarketOption[];
  selected: MarketOption | null;
  year: string; // "" = latest survey
};

export const initialMarketsState = (): MarketsState => ({
  query: "",
  searchedQuery: null,
  results: [],
  selected: null,
  year: "",
});

export type TopTab = "company" | "help";

export type SubTab = {
  key: string;
  label: string;
};

export const SUB_TABS: Record<TopTab, SubTab[]> = {
  company: [
    { key: "search", label: "Search" },
    { key: "screener", label: "Screener" },
    { key: "markets", label: "Markets" },
    { key: "info", label: "Info" },
    { key: "snapshot", label: "Snapshot" },
  ],
  help: [
    { key: "start", label: "Getting Started" },
    { key: "glossary", label: "Glossary" },
  ],
};
